export type SortDirection = "asc" | "desc";

type Comparator<T> = (a: T, b: T) => number;

export interface SortFieldConfig<T, K extends keyof T> {
  field: K;
  comparator: Comparator<T>;
}

/**
 * Generic sort function that works with any type and field configuration.
 * Supports both actual object keys and virtual sort fields (like "registered" which maps to "lastLoginAt").
 *
 * @param items - Array of items to sort
 * @param sortField - Field to sort by (can be a key of T or a virtual field)
 * @param sortDirection - Sort direction ("asc" or "desc")
 * @param config - Map of field names to their comparator functions
 * @returns New sorted array (original array is not mutated)
 */
export function sortBy<T, K extends string | number | symbol>(
  items: T[],
  sortField: K,
  sortDirection: SortDirection,
  config: Record<K, Comparator<T>>
): T[] {
  const comparator = config[sortField];

  if (!comparator) {
    console.warn(`No comparator found for sort field: ${String(sortField)}`);
    return [...items];
  }

  return [...items].sort((a, b) => {
    const comparison = comparator(a, b);
    return sortDirection === "asc" ? comparison : -comparison;
  });
}

export const comparators = {
  string: <T>(
    getValue: (item: T) => string | null | undefined
  ): Comparator<T> => {
    return (a, b) => {
      const aVal = getValue(a) ?? "";
      const bVal = getValue(b) ?? "";
      return aVal.localeCompare(bVal);
    };
  },

  number: <T>(
    getValue: (item: T) => number | null | undefined
  ): Comparator<T> => {
    return (a, b) => {
      const aVal = getValue(a) ?? 0;
      const bVal = getValue(b) ?? 0;
      return aVal - bVal;
    };
  },

  date: <T>(
    getValue: (item: T) => string | Date | null | undefined
  ): Comparator<T> => {
    return (a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      const aTime = aVal ? new Date(aVal).getTime() : 0;
      const bTime = bVal ? new Date(bVal).getTime() : 0;
      return aTime - bTime;
    };
  },

  boolean: <T>(
    getValue: (item: T) => boolean | null | undefined
  ): Comparator<T> => {
    return (a, b) => {
      const aVal = Number(getValue(a) ?? false);
      const bVal = Number(getValue(b) ?? false);
      return aVal - bVal;
    };
  },
};
